import { AccountTree, Add, Article, Difference, SmartToy, Star, StarBorder, Terminal } from "@mui/icons-material";
import {
    Alert,
    Box,
    Button,
    Chip,
    Divider,
    FormControl,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    Tooltip,
    Typography,
} from "@mui/material";
import StudioPromptBox from "modules/studio/components/StudioPromptBox";
import { StudioAiModel, StudioChangeProposal, StudioSession } from "modules/studio/services/studio-api";
import { StudioChatMessage } from "modules/studio/types/studio-types";

type ProposalStatus = "approved" | "rejected";

type StudioAiPanelProps = {
    mode: "architecture" | "ai";
    aiModels: StudioAiModel[];
    selectedAiModel: StudioAiModel | null;
    selectedAiModelId: string;
    favoriteAiModelId?: string | null;
    favoriteAiProviderId?: string | null;
    selectedSessionId: string;
    studioSessions: StudioSession[];
    aiModelsLoading: boolean;
    conversationFetching: boolean;
    architecturePending: boolean;
    proposalPending: boolean;
    proposalStatusPending: boolean;
    proposalApplyPending: boolean;
    chatMessages: StudioChatMessage[];
    hasProposalInThread: boolean;
    packageRequired: boolean;
    tt: (key: string, fallback: string) => string;
    onModelChange: (modelId: string) => void;
    onFavoriteAiSelection: () => void;
    onSessionChange: (sessionId: string) => void;
    onStartNewConversation: () => void;
    onGenerateProposal: (plan: Record<string, any> | null | undefined) => void;
    onStopProposal: () => void;
    onStopArchitecture: () => void;
    onSetProposalGroupStatus: (proposals: StudioChangeProposal[], status: ProposalStatus) => void;
    onApplyProposal: (proposals: StudioChangeProposal[]) => void;
    onPromptSubmit: (prompt: string) => void;
};

const StudioAiPanel = ({
    mode,
    aiModels,
    selectedAiModel,
    selectedAiModelId,
    favoriteAiModelId,
    favoriteAiProviderId,
    selectedSessionId,
    studioSessions,
    aiModelsLoading,
    conversationFetching,
    architecturePending,
    proposalPending,
    proposalStatusPending,
    proposalApplyPending,
    chatMessages,
    hasProposalInThread,
    packageRequired,
    tt,
    onModelChange,
    onFavoriteAiSelection,
    onSessionChange,
    onStartNewConversation,
    onGenerateProposal,
    onStopProposal,
    onStopArchitecture,
    onSetProposalGroupStatus,
    onApplyProposal,
    onPromptSubmit,
}: StudioAiPanelProps) => {
    const translateProposalOperation = (operationType: string) =>
        tt(`studio.proposal.operation.${operationType}`, operationType);
    const translateProposalStatus = (status: string) =>
        tt(`studio.proposal.status.${status}`, status);

    return (
        <Box key={mode} className="platform-studio__ai platform-studio__panel--enter">
        <Typography className="platform-studio__panel-title">
            {mode === "ai"
                ? tt("studio.ai.assistant_title", "AI Assistant")
                : tt("studio.ai.panel_title", "Architecture")}
        </Typography>
        <Divider />
        {aiModels.length > 0 ? (
            <Box className="platform-studio__ai-model-bar">
                <FormControl size="small" fullWidth>
                    <InputLabel id="studio-ai-model-label">
                        {tt("studio.ai.model", "Model")}
                    </InputLabel>
                    <Select
                        labelId="studio-ai-model-label"
                        label={tt("studio.ai.model", "Model")}
                        value={selectedAiModelId}
                        onChange={(event) => onModelChange(event.target.value)}
                    >
                        {aiModels.map((model) => (
                            <MenuItem key={model.id} value={model.id}>
                                {model.label || model.name} · {model.provider?.label || model.provider?.name}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <Tooltip title={tt("studio.ai.favorite_selection", "Favorite selected provider and model")}>
                    <span>
                        <IconButton
                            size="small"
                            onClick={onFavoriteAiSelection}
                            disabled={!selectedAiModelId}
                            className="platform-studio__favorite-button"
                        >
                            {selectedAiModelId === favoriteAiModelId && selectedAiModel?.provider?.id === favoriteAiProviderId
                                ? <Star fontSize="small" />
                                : <StarBorder fontSize="small" />}
                        </IconButton>
                    </span>
                </Tooltip>
            </Box>
        ) : (
            <Alert severity="info" className="platform-studio__alert">
                {aiModelsLoading
                    ? tt("studio.ai.models_loading", "Loading available models...")
                    : tt("studio.ai.no_models", "No AI models are available for your user.")}
            </Alert>
        )}
        <Box className="platform-studio__history-bar">
            <FormControl size="small" fullWidth>
                <InputLabel id="studio-history-label">
                    {tt("studio.history.label", "History")}
                </InputLabel>
                <Select
                    labelId="studio-history-label"
                    label={tt("studio.history.label", "History")}
                    value={selectedSessionId}
                    onChange={(event) => onSessionChange(event.target.value)}
                    disabled={studioSessions.length === 0 || conversationFetching}
                >
                    {studioSessions.length === 0 ? (
                        <MenuItem value="">
                            {tt("studio.history.empty", "No conversations")}
                        </MenuItem>
                    ) : studioSessions.map((session) => (
                        <MenuItem key={session.id} value={session.id}>
                            {session.title || session.id}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
            <Tooltip title={tt("studio.history.new", "New conversation")}>
                <span>
                    <IconButton
                        size="small"
                        onClick={onStartNewConversation}
                        disabled={architecturePending || proposalPending}
                    >
                        <Add fontSize="small" />
                    </IconButton>
                </span>
            </Tooltip>
        </Box>
        <Box className="platform-studio__ai-thread">
            <Box className="platform-studio__ai-message platform-studio__ai-message--system">
                {mode === "ai" ? <SmartToy fontSize="small" /> : <Article fontSize="small" />}
                <Typography variant="body2">
                    {mode === "ai"
                        ? tt("studio.ai.context_message", "Use the selected table, record and field as the working context for development.")
                        : tt("studio.ai.system_message", "Requests enter an architecture phase before proposal, validation and apply.")}
                </Typography>
            </Box>
            {chatMessages.map((message) => (
                <Box
                    key={message.id}
                    className={`platform-studio__ai-message platform-studio__ai-message--${message.role}`}
                >
                    {message.role === "user" ? <Terminal fontSize="small" /> : <AccountTree fontSize="small" />}
                    <Box minWidth={0}>
                        <Typography variant="body2">{message.content}</Typography>
                        {message.architecture ? (
                            <Box className="platform-studio__architecture-output">
                                {message.architecture.objects?.length ? (
                                    <Box>
                                        <Typography className="platform-studio__architecture-label">
                                            {tt("studio.architecture.objects", "Objects")}
                                        </Typography>
                                        <Stack spacing={0.75}>
                                            {message.architecture.objects.map((object, index) => (
                                                <Box key={`${object.type}-${object.name}-${index}`} className="platform-studio__architecture-item">
                                                    <strong>{object.type}</strong>
                                                    <span>{object.name}</span>
                                                    <small>{object.purpose}</small>
                                                </Box>
                                            ))}
                                        </Stack>
                                    </Box>
                                ) : null}
                                {message.architecture.conditions?.length ? (
                                    <Box>
                                        <Typography className="platform-studio__architecture-label">
                                            {tt("studio.architecture.conditions", "Conditions")}
                                        </Typography>
                                        <pre>{JSON.stringify(message.architecture.conditions, null, 2)}</pre>
                                    </Box>
                                ) : null}
                                {message.architecture.risks?.length ? (
                                    <Box>
                                        <Typography className="platform-studio__architecture-label">
                                            {tt("studio.architecture.risks", "Risks")}
                                        </Typography>
                                        <ul>
                                            {message.architecture.risks.map((risk, index) => <li key={`${risk}-${index}`}>{risk}</li>)}
                                        </ul>
                                    </Box>
                                ) : null}
                                {message.plan?.id ? (
                                    <Box className="platform-studio__proposal-actions">
                                        <Button
                                            variant="contained"
                                            size="small"
                                            onClick={() => onGenerateProposal(message.plan)}
                                            disabled={proposalPending}
                                        >
                                            {proposalPending
                                                ? tt("studio.action.generating_proposal", "Generating proposal")
                                                : tt("studio.action.generate_proposal", "Generate proposal")}
                                        </Button>
                                        {proposalPending ? (
                                            <Button variant="outlined" size="small" onClick={onStopProposal}>
                                                {tt("studio.action.stop", "Stop")}
                                            </Button>
                                        ) : null}
                                    </Box>
                                ) : null}
                            </Box>
                        ) : null}
                        {message.proposals?.length ? (
                            <Box className="platform-studio__proposal-output">
                                <Typography className="platform-studio__architecture-label">
                                    {tt("studio.proposal.items", "Proposal items")}
                                </Typography>
                                <Box className="platform-studio__proposal-summary-actions">
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        onClick={() => onSetProposalGroupStatus(message.proposals || [], "rejected")}
                                        disabled={proposalStatusPending || message.proposals.every((proposal) => proposal.status === "rejected")}
                                    >
                                        {tt("studio.action.reject_proposal", "Reject proposal")}
                                    </Button>
                                    <Button
                                        variant="contained"
                                        size="small"
                                        onClick={() => onSetProposalGroupStatus(message.proposals || [], "approved")}
                                        disabled={proposalStatusPending || message.proposals.every((proposal) => proposal.status === "approved")}
                                    >
                                        {tt("studio.action.accept_proposal", "Accept proposal")}
                                    </Button>
                                    <Button
                                        variant="contained"
                                        size="small"
                                        color="success"
                                        onClick={() => onApplyProposal(message.proposals || [])}
                                        disabled={proposalApplyPending || !message.proposals.every((proposal) => proposal.status === "approved")}
                                    >
                                        {proposalApplyPending
                                            ? tt("studio.action.applying_proposal", "Applying proposal")
                                            : tt("studio.action.apply_proposal", "Apply proposal")}
                                    </Button>
                                </Box>
                                <Stack spacing={0.75}>
                                    {message.proposals.map((proposal) => (
                                        <Box key={proposal.id} className="platform-studio__proposal-item">
                                            <Box minWidth={0}>
                                                <strong>{translateProposalOperation(proposal.operation_type)}</strong>
                                                <span>{proposal.target_key}</span>
                                                <small>{proposal.target_table}</small>
                                            </Box>
                                            <Chip
                                                size="small"
                                                label={translateProposalStatus(proposal.status)}
                                                className={`platform-studio__proposal-status platform-studio__proposal-status--${proposal.status}`}
                                            />
                                        </Box>
                                    ))}
                                </Stack>
                            </Box>
                        ) : null}
                    </Box>
                </Box>
            ))}
            {architecturePending ? (
                <Box className="platform-studio__ai-message">
                    <AccountTree fontSize="small" />
                    <Typography variant="body2">{tt("studio.action.planning", "Planning")}</Typography>
                </Box>
            ) : null}
            {proposalPending ? (
                <Box className="platform-studio__ai-message">
                    <Difference fontSize="small" />
                    <Typography variant="body2">{tt("studio.action.generating_proposal", "Generating proposal")}</Typography>
                </Box>
            ) : null}
        </Box>
        <StudioPromptBox
            disabled={packageRequired || architecturePending || proposalPending}
            isArchitecturePending={architecturePending}
            isProposalPending={proposalPending}
            hasProposalInThread={hasProposalInThread}
            canUseAi={aiModels.length > 0}
            packageRequired={packageRequired}
            tt={tt}
            onSubmit={onPromptSubmit}
            onStop={proposalPending ? onStopProposal : onStopArchitecture}
        />
        </Box>
    );
};

export default StudioAiPanel;
